/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { Checkbox } from '@fluentui/react/lib/Checkbox';
import { IContextualMenuItem } from '@fluentui/react/lib/ContextualMenu';
import { DetailsList, DetailsRow, IDetailsRowStyleProps, IDetailsRowStyles } from '@fluentui/react/lib/DetailsList';
import { Label } from '@fluentui/react/lib/Label';
import { Pivot, PivotItem } from '@fluentui/react/lib/Pivot';
import { IStackProps, IStackStyles, Stack } from '@fluentui/react/lib/Stack';
import { SelectionMode, classNamesFunction, getId } from '@fluentui/react/lib/Utilities';
import flatten from 'lodash/flatten';
import { observer } from 'mobx-react';
import * as React from 'react';
import { defineMessages } from 'react-intl';
import { BasicModal, BasicModalProps } from 'src/core/components/BasicModal';
import { ExternalLink } from 'src/core/components/ExternalLink';
import { LuisDebouncedSearchBox } from 'src/core/components/LuisDebouncedSearchBox';
import { LuisText } from 'src/core/components/LuisText';
import { LuisTextField } from 'src/core/components/LuisTextField';
import { SearchResultsAnnouncer } from 'src/core/components/SearchResultsAnnouncer';
import { TruncatedText } from 'src/core/components/TruncatedText';
import { useGlobalState } from 'src/core/hooks/useGlobalState';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { getScopedTheme } from 'src/core/themes/luisStylist';
import { withModalMounter } from 'src/core/utils/hocs/withModalMounter';
import { getRandomNumber } from 'src/core/utils/randomValueGenerators';
import { QnaMakerQnaPairInternalRef, QnaMakerQnaSwagger } from 'src/qnaMaker/core/services/qnas/QnaMakerAssetsSwaggers';
import { QnaMakerQnaPrompt } from 'src/qnaMaker/core/stores/QnaMakerQnaStore';

export type QnaMakerFollowUpPromptModalProps = BasicModalProps<{
    prompt: QnaMakerQnaPrompt;
    linkedPair?: Partial<QnaMakerQnaSwagger>;
}> & {
    prompt: QnaMakerQnaPrompt;
    isContextOnly: boolean;
    selectedQnaId: number;
    hiddenQnaPairIds: number[];
    qnaPairsJson: QnaMakerQnaSwagger[];
};

type FilteredQnaType = {
    key: string;
    text?: string;
    qnaId?: number;
    question?: string;
    answer?: string;
};

const messages = defineMessages({
    title: {
        id: 'qnaMaker.qnaMakerFollowUpPromptModal.title',
        defaultMessage: 'Follow-up prompt'
    },
    description: {
        id: 'qnaMaker.qnaMakerFollowUpPromptModal.description',
        defaultMessage:
            'Add a follow up prompt when a user would want more information after receiving an answer. Prompts link to a new or existing question pair.'
    },
    promptLearnMore: {
        id: 'qnaMaker.qnaMakerFollowUpPromptModal.promptLearnMore',
        defaultMessage: 'Learn more about prompts'
    },
    displayedText: {
        id: 'qnaMaker.qnaMakerFollowUpPromptModal.displayedText',
        defaultMessage: 'Text displayed in the prompt to the user'
    },
    displayedTextPlaceholder: {
        id: 'qnaMaker.qnaMakerFollowUpPromptModal.displayedTextPlaceholder',
        defaultMessage: 'Enter text for how the prompt displays'
    },
    contextSectionTitle: {
        id: 'qnaMaker.qnaMakerFollowUpPromptModal.contextSectionTitle',
        defaultMessage: 'Show only in contextual flow?'
    },
    contextSectionDescription: {
        id: 'qnaMaker.qnaMakerFollowUpPromptModal.contextSectionDescription',
        defaultMessage:
            'A context only prompt cannot be queried directly. The user will only see it after the answer to the parent question is returned.'
    },
    contextLearnMore: {
        id: 'qnaMaker.qnaMakerFollowUpPromptModal.contextLearnMore',
        defaultMessage: 'Learn more about context'
    },
    checkboxDescription: {
        id: 'qnaMaker.qnaMakerFollowUpPromptModal.checkboxDescription',
        defaultMessage: 'Show in contextual flow only'
    },
    searchPlaceholder: {
        id: 'qnaMaker.qnaMakerFollowUpPromptModal.searchPlaceholder',
        defaultMessage: 'Search for an existing pair'
    },
    question: {
        id: 'qnaMaker.qnaMakerFollowUpPromptModal.question',
        defaultMessage: 'Question'
    },
    answer: {
        id: 'qnaMaker.qnaMakerFollowUpPromptModal.answer',
        defaultMessage: 'Answer'
    },
    noQueryPlaceholder: {
        id: 'qnaMaker.qnaMakerFollowUpPromptModal.noQueryPlaceholder',
        defaultMessage: 'Please enter query to search for pairs'
    },
    noItemsFound: {
        id: 'qnaMaker.qnaMakerFollowUpPromptModal.noItemsFound',
        defaultMessage: 'No pair match this query'
    },
    linkExistingPair: {
        id: 'qnaMaker.qnaMakerFollowUpPromptModal.linkExistingPair',
        defaultMessage: 'Link existing pair'
    },
    createNewPair: {
        id: 'qnaMaker.qnaMakerFollowUpPromptModal.createNewPair',
        defaultMessage: 'Create link to new pair'
    },
    addPrompt: {
        id: 'qnaMaker.qnaMakerFollowUpPromptModal.addPrompt',
        defaultMessage: 'Add prompt'
    }
});

const theme = getScopedTheme('QnaMakerFollowUpPromptModal');
const LintToPairContainerHeight = '140px';

const rowStyles = classNamesFunction<IDetailsRowStyleProps, IDetailsRowStyles>()({
    root: {
        borderBottom: 'none',
        backgroundColor: 'transparent',
        $nest: {
            '&:hover,&:focus,&:select': {
                backgroundColor: 'transparent'
            }
        }
    },
    cell: { padding: '0' }
});

const getPairRendererContainer = (isHoveringEnabled: boolean) =>
    classNamesFunction<IStackProps, IStackStyles>()({
        root: {
            borderBottom: 'none',
            width: '100%',
            padding: '5px 10px',
            cursor: isHoveringEnabled && 'pointer',
            $nest: isHoveringEnabled && {
                '&:hover': {
                    backgroundColor: theme.pairHoveringBackground
                }
            }
        }
    });

/**
 * Represents component for adding or editing a follow up prompt for the QnAs answer.
 */
export const QnaMakerFollowUpPromptModal = withModalMounter(
    observer((props: QnaMakerFollowUpPromptModalProps) => {
        const { prompt, qnaPairsJson, hiddenQnaPairIds, selectedQnaId, onSubmit } = props;
        const { localize } = useLocalization();
        const { rootStore } = useGlobalState();
        const { languageDirection } = rootStore.currentQnaMakerProjectStore;

        const [editablePrompt, setEditablePrompt] = React.useState(prompt);
        const [isNewPairScreenDisplayed, setIsNewPairScreenDisplayed] = React.useState(false);
        const [query, setQuery] = React.useState('');
        const [newPairAnswer, setNewPairAnswer] = React.useState('');

        const linkedQnaPair = qnaPairsJson.find(qna => qna.id === editablePrompt?.qnaId);
        const [isContextFlagEnabled, setIsContextFlagEnabled] = React.useState(linkedQnaPair?.dialog?.isContextOnly);
        const [filteredQnas, setFilteredQnas] = React.useState<FilteredQnaType[]>([]);

        const isModalValid =
            Boolean(editablePrompt?.displayText?.trim()) &&
            Boolean(isNewPairScreenDisplayed ? newPairAnswer.trim() : editablePrompt?.qnaId);

        React.useEffect(() => {
            if (linkedQnaPair) {
                setIsContextFlagEnabled(linkedQnaPair.dialog?.isContextOnly);
            }
        }, [linkedQnaPair]);

        const handleKeyDown = (item: any) => {
            setEditablePrompt(v => ({ ...v, qnaId: item?.qnaId }));
        };

        const renderQuestionAnswerItem = ({
            key,
            qnaId,
            question,
            answer
        }: {
            key?: string;
            qnaId: number;
            question: string;
            answer: string;
        }) => {
            if (key === 'noItemsFound' || key === 'noQuery') {
                return (
                    <Stack horizontalAlign="center" tokens={{ padding: '10px 0 0 0' }}>
                        <LuisText>{localize(key === 'noItemsFound' ? messages.noItemsFound : messages.noQueryPlaceholder)}</LuisText>
                    </Stack>
                );
            }

            return (
                <Stack
                    key={selectedQnaId}
                    styles={getPairRendererContainer(!editablePrompt?.qnaId)}
                    onClick={() => setEditablePrompt(v => ({ ...v, qnaId }))}>
                    {question && (
                        <Stack horizontal>
                            <LuisText fontWeight="bold">{localize(messages.question)}:</LuisText>
                            &nbsp;
                            <TruncatedText dir={languageDirection} title={question} maxWidth="90%">
                                {question}
                            </TruncatedText>
                        </Stack>
                    )}
                    <Stack horizontal tokens={{ padding: !question && '10px 0  0 0' }}>
                        <LuisText fontWeight="bold">{localize(messages.answer)}:</LuisText>
                        &nbsp;
                        <TruncatedText dir={languageDirection} title={answer} maxWidth="90%">
                            {answer}
                        </TruncatedText>
                    </Stack>
                </Stack>
            );
        };

        const getFilteredQnas = (queryString: string) => {
            let tmpFilteredQnas: IContextualMenuItem[] = [];
            if (!queryString.length) {
                tmpFilteredQnas = [{ key: 'noQuery', text: '' }];
            } else {
                tmpFilteredQnas = flatten(
                    qnaPairsJson
                        .filter(qna => !hiddenQnaPairIds.includes(qna.id))
                        .map(pair => {
                            if (pair.answer.toLocaleLowerCase().includes(queryString.toLocaleLowerCase())) {
                                return {
                                    key: getId(),
                                    text: '',
                                    qnaId: pair.id,
                                    question: pair.questions[0],
                                    answer: pair.answer
                                };
                            }

                            return pair.questions
                                .filter(q => q.toLocaleLowerCase().includes(queryString.toLocaleLowerCase()))
                                .map(q => ({
                                    key: getId(),
                                    text: '',
                                    qnaId: pair.id,
                                    question: q,
                                    answer: pair.answer
                                }));
                        })
                ).filter(Boolean);

                if (!tmpFilteredQnas.length) {
                    tmpFilteredQnas = [{ key: 'noItemsFound', text: '' }];
                }
            }

            setQuery(queryString);
            setFilteredQnas(tmpFilteredQnas);
        };

        const handleSubmit = () => {
            if (isNewPairScreenDisplayed) {
                const newPair: Partial<QnaMakerQnaSwagger> = {
                    id: getRandomNumber(),
                    internalRef: QnaMakerQnaPairInternalRef.uiPromptPair,
                    questions: [editablePrompt.displayText],
                    answer: newPairAnswer,
                    parentQnaIds: [selectedQnaId],
                    dialog: { isContextOnly: isContextFlagEnabled, prompts: [] }
                };

                onSubmit({
                    prompt: { ...editablePrompt, displayText: editablePrompt.displayText, qnaId: newPair.id, qna: newPair }
                });

                return;
            }

            const linkedPair: Partial<QnaMakerQnaSwagger> = {
                ...linkedQnaPair,
                dialog: { ...linkedQnaPair.dialog, isContextOnly: isContextFlagEnabled },
                parentQnaIds: linkedQnaPair.parentQnaIds
                    ? [
                          ...linkedQnaPair.parentQnaIds,
                          linkedQnaPair.parentQnaIds.find(id => id === selectedQnaId) ? undefined : selectedQnaId
                      ].filter(Boolean)
                    : [selectedQnaId]
            };

            onSubmit({ prompt: { ...editablePrompt, displayText: editablePrompt.displayText }, linkedPair });
        };

        const isFilteredQnasAnnouncerActive = () => {
            if (filteredQnas.length && filteredQnas[0].key === 'noQuery') {
                return false;
            }

            return true;
        };

        const getValidItemsNumberInFilteredQnas = () => {
            if (!filteredQnas.length || filteredQnas[0].key === 'noQuery' || filteredQnas[0].key === 'noItemsFound') {
                return 0;
            }

            return filteredQnas.length;
        };

        return (
            <BasicModal
                {...props}
                minWidth={750}
                valid={isModalValid}
                onSubmit={handleSubmit}
                doneButtonText={localize(messages.addPrompt)}
                dialogContentProps={{ title: localize(messages.title) }}>
                <Stack styles={{ root: { overflowX: 'hidden' } }} tokens={{ childrenGap: 14 }}>
                    <LuisText>
                        {localize(messages.description)} &nbsp;
                        <ExternalLink href="https://aka.ms/qa-multiturn">{localize(messages.promptLearnMore)}</ExternalLink>
                    </LuisText>

                    <Stack tokens={{ childrenGap: 8 }}>
                        <LuisTextField
                            required
                            dir={languageDirection}
                            value={editablePrompt?.displayText}
                            label={localize(messages.displayedText)}
                            placeholder={localize(messages.displayedTextPlaceholder)}
                            onChange={(_, v) => setEditablePrompt(p => ({ ...p, displayText: v }))}
                        />

                        {prompt?.qnaId ? (
                            renderQuestionAnswerItem({
                                answer: linkedQnaPair.answer,
                                question: undefined,
                                qnaId: linkedQnaPair.id
                            })
                        ) : (
                            <Pivot
                                styles={{ root: { marginLeft: '5px' } }}
                                onLinkClick={item => setIsNewPairScreenDisplayed(item.props.itemKey === 'newPair')}>
                                <PivotItem headerText={localize(messages.linkExistingPair)} itemKey="existingPair">
                                    {editablePrompt?.qnaId ? (
                                        <Stack verticalFill tokens={{ childrenGap: 8 }}>
                                            {renderQuestionAnswerItem({
                                                answer: linkedQnaPair.answer,
                                                question: undefined,
                                                qnaId: linkedQnaPair.id
                                            })}
                                        </Stack>
                                    ) : (
                                        <Stack styles={{ root: { border: '1px solid', marginTop: '10px' } }}>
                                            <LuisDebouncedSearchBox
                                                size={30}
                                                value={query}
                                                dir={languageDirection}
                                                ariaLabel={localize(messages.searchPlaceholder)}
                                                placeholder={localize(messages.searchPlaceholder)}
                                                onChange={(_, newValue?: string) => getFilteredQnas(newValue)}
                                                styles={{ root: { border: 'none' }, field: { border: 'none' } }}
                                            />

                                            <SearchResultsAnnouncer
                                                isActive={isFilteredQnasAnnouncerActive()}
                                                itemsLength={getValidItemsNumberInFilteredQnas()}
                                            />

                                            <DetailsList
                                                items={filteredQnas}
                                                isHeaderVisible={false}
                                                columns={[
                                                    {
                                                        key: 'items',
                                                        minWidth: 100,
                                                        name: 'itemsColumn',
                                                        onRender: renderQuestionAnswerItem
                                                    }
                                                ]}
                                                onItemInvoked={item => handleKeyDown(item)}
                                                selectionMode={SelectionMode.none}
                                                styles={{
                                                    root: {
                                                        height: LintToPairContainerHeight,
                                                        overflowY: 'auto',
                                                        overflowX: 'hidden',
                                                        headerWrapper: { display: 'none' }
                                                    }
                                                }}
                                                onRenderRow={rowProps => <DetailsRow {...rowProps} styles={rowStyles} />}
                                            />
                                        </Stack>
                                    )}
                                </PivotItem>

                                <PivotItem headerText={localize(messages.createNewPair)} itemKey="newPair">
                                    <LuisTextField
                                        autoFocus
                                        multiline
                                        value={newPairAnswer}
                                        dir={languageDirection}
                                        autoAdjustHeight={false}
                                        onChange={(_, val) => setNewPairAnswer(val)}
                                        styles={{ root: { marginTop: '10px' }, field: { minHeight: '172px' } }}
                                    />
                                </PivotItem>
                            </Pivot>
                        )}

                        <Stack tokens={{ childrenGap: 12 }}>
                            <Stack tokens={{ childrenGap: 4 }}>
                                <Label>{localize(messages.contextSectionTitle)}</Label>
                                <LuisText>
                                    {localize(messages.contextSectionDescription)}
                                    &nbsp;
                                    <ExternalLink href="https://aka.ms/qa-multiturn">{localize(messages.contextLearnMore)}</ExternalLink>
                                </LuisText>
                            </Stack>

                            <Checkbox
                                key={Math.random()}
                                checked={isContextFlagEnabled}
                                styles={{ root: { margin: '5px' } }}
                                label={localize(messages.checkboxDescription)}
                                onChange={() => setIsContextFlagEnabled(f => !f)}
                            />
                        </Stack>
                    </Stack>
                </Stack>
            </BasicModal>
        );
    })
);
